import React, {useCallback, useMemo} from 'react';

import {Box, Typography} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon/FuseSvgIcon';
import {ADD_TEEN_EVENTS} from '@/app/auth/events/AddTeenEvents';
import {AppButton} from '@/app/shared-components';
import InputTextField, {
  TransformTypeInputValues,
} from '@/app/shared-components/InputTextField/InputTextField';
import MdkModal from '@/app/shared-components/MdkModal/MdkModal';
import {PROJECT_TAGS} from '@/constants/ProjectsConstants';
import {useAddKidRepository} from '@/dataSource/src/api/add-kid/useRepository';
import {useTrackingManager} from '@/hooks/TrackingManager/useTrackingManager';
import {COLORS} from '@/styles/colors';

import './styles';

const steps = [
  'Ask your kid to open their Modak app and tap on the "Invite your parent" banner.',
  `Enter here the ID displayed on your kid's screen.`,
  `Verify ID and voilà! We'll link your accounts.`,
];

const minLength = 5;
const maxLength = 10;

interface FamilyCodeModalProps {
  open: boolean;
  onClose: () => void;
  kidName?: string;
  onKidLinked?: () => void;
}

const FamilyCodeModal: React.FC<FamilyCodeModalProps> = ({
  open,
  onClose,
  kidName,
  onKidLinked,
}) => {
  const {t} = useTranslation();
  const {trackEvent} = useTrackingManager();

  const [familyCode, setFamilyCode] = React.useState('');

  const trackEventConfirmAddKidWithId = useCallback(() => {
    trackEvent(ADD_TEEN_EVENTS.SELECT_ADD_KID_FAMILY_CODE_CONFIRM, {
      project_tag: PROJECT_TAGS.ONBOARDING,
      Invitation: {
        code: familyCode,
        written: true,
      },
    });
  }, [familyCode, trackEvent]);

  //#region [---- [REPOSITORIES] ----]
  const {isAddKidWithInvitationLoading, handleAddKidWithInvitation} =
    useAddKidRepository();
  //#endRegion

  const handleOnClickVerifyId = useCallback(async () => {
    trackEventConfirmAddKidWithId();
    const linked = await handleAddKidWithInvitation(familyCode);
    if (linked) {
      onClose();
      onKidLinked?.();
    }
  }, [
    familyCode,
    handleAddKidWithInvitation,
    onClose,
    onKidLinked,
    trackEventConfirmAddKidWithId,
  ]);

  const isDisabledButton = useMemo(() => {
    return !familyCode || familyCode.length < minLength;
  }, [familyCode]);

  return (
    <MdkModal
      open={open}
      onClose={onClose}>
      <Box className="family-code-modal">
        <Box className="family-code-modal-container">
          <Box className="family-code-modal-title-container">
            <Typography className="family-code-modal-title">
              <Trans
                i18nKey="family_code_modal.title"
                values={{name: kidName}}
                components={{span: <span className="your-kid" />}}
              />
            </Typography>
          </Box>
          <Box className="family-code-modal-content">
            <Box className="family-code-modal-input-container">
              <InputTextField
                label={t('family_code_modal.input', {name: kidName})}
                placeholder={t('family_code_modal.input', {name: kidName})}
                maxLength={maxLength}
                value={familyCode}
                setValue={setFamilyCode}
                transformType={TransformTypeInputValues.UPPERCASE}
              />
              <Box className="family-code-modal-input-helper-container">
                {isDisabledButton ? (
                  <Typography>•</Typography>
                ) : (
                  <Typography style={{color: COLORS.ON_SURFACE_1}}>
                    <FuseSvgIcon size={16}>heroicons-outline:check</FuseSvgIcon>
                  </Typography>
                )}
                <Typography
                  className="family-code-modal-input-helper-text"
                  style={{
                    color: isDisabledButton
                      ? COLORS.ON_SURFACE_VARIANT
                      : COLORS.ON_SURFACE_1,
                  }}>
                  {t('family_code_modal.helper_text', {min_length: minLength})}
                </Typography>
              </Box>
            </Box>

            <Box className="family-code-modal-steps-container">
              <Typography className="family-code-modal-steps-title">
                {t('family_code_modal.steps_title')}
              </Typography>
              {steps.map((step, index) => (
                <Box
                  key={`family-code-step-${index}`}
                  className="family-code-modal-steps-item">
                  <Box className="family-code-modal-steps-item-container">
                    <Box className="family-code-modal-steps-item-number">
                      <Typography className="family-code-modal-steps-item-number-text">
                        {index + 1}
                      </Typography>
                    </Box>
                    <Typography className="family-code-modal-steps-item-text">
                      {step}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        <AppButton
          label={t('family_code_modal.button')}
          onClick={handleOnClickVerifyId}
          disabled={isDisabledButton}
          isLoading={isAddKidWithInvitationLoading}
        />
      </Box>
    </MdkModal>
  );
};

export default FamilyCodeModal;
